import {
  ArrowCirclepathRightIcon,
  PauseIcon,
  SmallPlayIcon,
} from "@repo/edtech-assets";

import {
  createGlobalPlayerStore,
  GlobalPlayerStore,
  KeyPressAction,
  Snapshot,
  VideoClickAction,
} from "../model";

declare global {
  interface Window {
    videoPlayerStore?: GlobalPlayerStore,
    audioPlayerStore?: GlobalPlayerStore,
    globalPlayerManagerInitialized?: boolean,
    WebComponentProxyApi?: string,
  }
}

export const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return "--:--";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const formattedHours = hours > 0 ?
    `${hours}:` :
    "";
  const formattedMinutes = minutes < 10 && hours > 0 ?
    `0${minutes}:` :
    `${minutes}:`;
  const formattedSeconds = secs < 10 ?
    `0${secs}` :
    `${secs}`;

  return `${formattedHours}${formattedMinutes}${formattedSeconds}`;
};

export const getVideoClickAction = (isMobile: boolean): VideoClickAction => {
  if (isMobile) return VideoClickAction.OnlyShowControls;
  return VideoClickAction.TogglePlay;
};

export const getKeyPressAction = (key: string): KeyPressAction => {
  switch (key) {
    case "ArrowLeft":
      return KeyPressAction.SkipPrevious;
    case "ArrowRight":
      return KeyPressAction.SkipNext;
    case " ":
      return KeyPressAction.TogglePlay;
    default:
      return KeyPressAction.None;
  }
};

export const convertBytesToMb = (bytes: number): string =>
  (bytes / 1024 / 1024).toFixed(2).replace(".", ",");

export const getVideoSnapshot = (
  url: string,
  width?: number,
  height?: number,
): Promise<Snapshot> => {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.src = url;
    video.crossOrigin = "anonymous";
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    const cleanup = () => {
      video.pause();
      video.removeAttribute("src");
      video.load();
    };

    video.addEventListener("loadedmetadata", () => {
      video.currentTime = 0.01;
    });

    video.addEventListener("seeked", () => {
      const finalWidth = width ?? video.videoWidth;
      const finalHeight = height ?? video.videoHeight;

      const canvas = document.createElement("canvas");
      canvas.width = finalWidth;
      canvas.height = finalHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        cleanup();
        return resolve({
          url: null,
          width: finalWidth,
          height: finalHeight,
        });
      }

      ctx.drawImage(
        video, 0, 0, finalWidth, finalHeight,
      );
      const dataUrl = canvas.toDataURL("image/jpeg");

      cleanup();
      resolve({
        url: dataUrl,
        width: finalWidth,
        height: finalHeight,
      });
    });

    video.addEventListener("error", () => {
      cleanup();
      resolve({
        url: null,
        width: width ?? 200,
        height: height ?? 200,
      });
    });

    video.load();
  });
};

const snapshotCache = new Map<string, Promise<Snapshot>>();

export function getVideoSnapshotCached(
  url: string, w?: number, h?: number,
) {
  if (!snapshotCache.has(url)) {
    snapshotCache.set(url, getVideoSnapshot(
      url, w, h,
    ));
  }
  return snapshotCache.get(url)!;
}

export const initPlayerStores = () => {
  if (typeof window === "undefined") return;

  if (window.videoPlayerStore && window.audioPlayerStore) return;

  if (!window.videoPlayerStore) {
    window.videoPlayerStore = createGlobalPlayerStore();
  }

  if (!window.audioPlayerStore) {
    window.audioPlayerStore = createGlobalPlayerStore();
  }
};

export const enum FormatMode {
  HMS = "hhmmss",
  MS = "mmss",
  S = "ss",
}

export const formatToHHMMSS = (seconds: number,
  mode: FormatMode = FormatMode.HMS): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");

  switch (mode) {
    case "ss":
      return `${seconds}`;
    case "mmss":
      return `${mm}:${ss}`;
    case "hhmmss":
    default:
      return `${hh}:${mm}:${ss}`;
  }
};

export const getPlayIcon = (playing: boolean, ended: boolean) => {
  let icon = SmallPlayIcon;
  if (playing) icon = PauseIcon;
  else if (ended) icon = ArrowCirclepathRightIcon;

  return icon;
};
